import '@fontsource/oswald/400.css'
import '@fontsource/oswald/500.css'
import '@fontsource/oswald/700.css'
import '@fontsource/dm-sans/400.css'
import '@fontsource/dm-sans/500.css'
import '@fontsource/dm-sans/600.css'
import '@fontsource/jetbrains-mono/400.css'
import './app/theme/colors.css'
import './index.css'
import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import App from './app/layout/App'
import reportWebVitals from './reportWebVitals'
import { store, StoreContext, useStore } from './app/stores/store'
import { MantineProvider, ColorSchemeProvider } from '@mantine/core'
import { DatesProvider } from '@mantine/dates'
import { theme } from './app/theme/theme'
import 'react-calendar/dist/Calendar.css'
import 'react-toastify/dist/ReactToastify.min.css'
import 'react-datepicker/dist/react-datepicker.css'
import './app/layout/styles.css'
import './app/theme/overrides.css'
import { Router } from 'react-router-dom'
import { history } from './app/history'
import ErrorBoundary from './app/common/ErrorBoundary'
import { observer } from 'mobx-react-lite'

type ColorScheme = 'dark' | 'light';

const GUEST_KEY = 'gs-colorScheme'
const userKey = (username: string) => `gs-colorScheme-${username}`

function storedScheme (username?: string | null): ColorScheme {
  const key = username ? userKey(username) : GUEST_KEY
  const stored = window.localStorage.getItem(key)
  return stored === 'light' ? 'light' : 'dark'
}

/**
 * Lives inside StoreContext so it can access userStore.
 * Persists the preference per-username (or under a guest key when logged out).
 * Switches automatically when the logged-in user changes.
 */
const ThemeWrapper = observer(function ThemeWrapper () {
  const { userStore } = useStore()
  const username = userStore.user?.username ?? null

  const [colorScheme, setColorScheme] = useState<ColorScheme>(() =>
    storedScheme(username)
  )

  // Whenever the logged-in user changes, load their saved preference.
  useEffect(() => {
    setColorScheme(storedScheme(username))
  }, [username])

  useEffect(() => {
    document.documentElement.setAttribute('data-mantine-color-scheme', colorScheme)
  }, [colorScheme])

  const toggleColorScheme = (value?: ColorScheme) => {
    const next = value ?? (colorScheme === 'dark' ? 'light' : 'dark')
    setColorScheme(next)
    // Persist under the user-specific key when logged in, guest key otherwise.
    const key = username ? userKey(username) : GUEST_KEY
    window.localStorage.setItem(key, next)
  }

  return (
    <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
      <MantineProvider
        withNormalizeCSS={false}
        withGlobalStyles
        theme={{ ...theme, colorScheme }}
      >
        <DatesProvider settings={{ locale: 'en', firstDayOfWeek: 0 }}>
          <App />
        </DatesProvider>
      </MantineProvider>
    </ColorSchemeProvider>
  )
})

function Root () {
  return (
    <Router history={history}>
      <StoreContext.Provider value={store}>
        <ThemeWrapper />
      </StoreContext.Provider>
    </Router>
  )
}

ReactDOM.render(
  <ErrorBoundary>
    <Root />
  </ErrorBoundary>,
  document.getElementById('root')
)

reportWebVitals()
